import React, {useState, useEffect} from 'react';
import './App.css';
import {
    ChevronDown,
    Brain,
    Users,
    Award,
    Phone,
    Calendar,
    Star,
    X,
    MessageCircle,
    MessageSquare,
    Expand,
    ChevronLeft,
    ChevronRight,
    Heart,
    Smile,
    Sparkles
} from 'lucide-react';
import {useI18n} from './hooks/useI18n';
import BookingForm from './components/BookingForm';

function App() {
    const {t, language, changeLanguage} = useI18n();
    const [isVisible, setIsVisible] = useState(false);
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedReview, setSelectedReview] = useState(null);
    const [showBooking, setShowBooking] = useState(false);
    const [bookingActivityType, setBookingActivityType] = useState('general');
    const [showCertificateGallery, setShowCertificateGallery] = useState(false);
    const [activeCertificate, setActiveCertificate] = useState(0);
    const [scrollY, setScrollY] = useState(0);

    const openBookingWithActivity = (activityType) => {
        setBookingActivityType(activityType);
        setShowBooking(true);
    };

    useEffect(() => {
        setIsVisible(true);
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);

        // Auto-rotate testimonials
        const testimonialInterval = setInterval(() => {
            setActiveTestimonial(prev =>
                prev === t('testimonials.items').length - 1 ? 0 : prev + 1
            );
        }, 5000);

        // Keyboard navigation for certificate gallery and video
        const handleKeyPress = (e) => {
            if (showCertificateGallery) {
                if (e.key === 'Escape') {
                    setShowCertificateGallery(false);
                } else if (e.key === 'ArrowLeft') {
                    setActiveCertificate(prev => prev === 0 ? 12 : prev - 1);
                } else if (e.key === 'ArrowRight') {
                    setActiveCertificate(prev => prev === 12 ? 0 : prev + 1);
                }
            } else if (showReviewModal && e.key === 'Escape') {
                setShowReviewModal(false);
            }
        };

        window.addEventListener('keydown', handleKeyPress);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('keydown', handleKeyPress);
            clearInterval(testimonialInterval);
        };
    }, [t, showCertificateGallery, showReviewModal]);


    const achievements = [
        {number: "19", label: t('achievements.experience')},
        {number: "104", label: t('achievements.reviews')},
        {number: "500+", label: t('achievements.children')},
        {number: "95%", label: t('achievements.success')}
    ];

    // Certificate gallery functions
    const openCertificateGallery = (index) => {
        setActiveCertificate(index);
        setShowCertificateGallery(true);
    };

    const closeCertificateGallery = () => {
        setShowCertificateGallery(false);
    };

    const nextCertificate = () => {
        setActiveCertificate(prev => prev === 12 ? 0 : prev + 1);
    };

    // Review modal functions
    const openReviewModal = (review) => {
        setSelectedReview(review);
        setShowReviewModal(true);
    };

    const closeReviewModal = () => {
        setShowReviewModal(false);
        setSelectedReview(null);
    };

    // Function to truncate text for preview
    const truncateText = (text, maxLength = 200) => {
        if (text.length <= maxLength) return text;
        return text.slice(0, maxLength) + '...';
    };

    const prevCertificate = () => {
        setActiveCertificate(prev => prev === 0 ? 12 : prev - 1);
    };


    const LanguageSwitcher = () => {
        const handleLanguageChange = (lang) => {
            changeLanguage(lang);
        };

        return (
            <div className="flex items-center space-x-1 bg-ivory/90 rounded-full p-1 shadow-lg">
                <button
                    type="button"
                    onClick={() => handleLanguageChange('ru')}
                    className={`px-3 py-2 text-sm rounded-full font-medium transition-all duration-200 ${
                        language === 'ru'
                            ? 'bg-emerald-600 text-white shadow-md'
                            : 'text-taupe hover:text-emerald-600 hover:bg-emerald-50'
                    }`}
                >
                    RU
                </button>
                <button
                    type="button"
                    onClick={() => handleLanguageChange('en')}
                    className={`px-3 py-2 text-sm rounded-full font-medium transition-all duration-200 ${
                        language === 'en'
                            ? 'bg-emerald-600 text-white shadow-md'
                            : 'text-taupe hover:text-emerald-600 hover:bg-emerald-50'
                    }`}
                >
                    EN
                </button>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-ivory via-warm-white to-soft-cream">

            {/* Minimal Navigation - Language Switcher Only */}
            <nav className="fixed top-0 right-0 z-50 transition-all duration-300">
                <div className="p-4">
                    <LanguageSwitcher/>
                </div>
            </nav>

            {/* Enhanced Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                {/* Enhanced background with parallax effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-teal to-emerald-700"
                     style={{
                         transform: `translateY(${scrollY * 0.5}px)`
                     }}></div>
                <div className="absolute inset-0 bg-black opacity-20"></div>

                {/* Fixed floating elements */}
                <div className="absolute inset-0">
                    {[...Array(12)].map((_, i) => {
                        const size = 4 + (i % 3) * 2;
                        const left = 10 + (i * 7) % 80;
                        const top = 15 + (i * 11) % 70;
                        return (
                            <div
                                key={i}
                                className="absolute bg-ivory rounded-full opacity-40 animate-float"
                                style={{
                                    width: `${size}px`,
                                    height: `${size}px`,
                                    left: `${left}%`,
                                    top: `${top}%`,
                                    animationDelay: `${i * 0.5}s`,
                                    animationDuration: `${4 + (i % 3)}s`
                                }}
                            />
                        );
                    })}
                </div>

                {/* Gradient overlay blobs */}
                <div className="absolute inset-0">
                    <div
                        className="absolute top-20 left-20 w-72 h-72 bg-blush/20 rounded-full filter blur-xl animate-blob"></div>
                    <div
                        className="absolute top-40 right-20 w-72 h-72 bg-ivory/20 rounded-full filter blur-xl animate-blob animation-delay-2000"></div>
                    <div
                        className="absolute -bottom-8 left-1/2 w-72 h-72 bg-teal/20 rounded-full filter blur-xl animate-blob animation-delay-4000"></div>
                </div>

                <div className={`relative z-10 text-center text-white px-4 transform transition-all duration-1000 ${
                    isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}>
                    {/* Enhanced profile image with glow effect */}
                    <div className="mb-8">
                        <div
                            className={`w-40 h-40 mx-auto mb-6 rounded-full bg-transparent border-4 border-ivory/40 flex items-center justify-center animate-glow transform transition-all duration-700 ${
                                isVisible ? 'animate-fadeInScale' : ''
                            }`}>
                            <img
                                src="https://cdn.hydralabs.ru/clients/neuro/profile.jpg"
                                alt="Elena Mykalova"
                                className="w-32 h-32 rounded-full object-cover transition-transform duration-500 hover:scale-110"
                            />
                        </div>
                    </div>

                    {/* Enhanced typography with staggered animations */}
                    <div className={`transform transition-all duration-1000 delay-300 ${
                        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
                    }`}>
                        <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 text-shadow-lg leading-tight">
                            {t('hero.name')}
                        </h1>
                    </div>

                    <div className={`transform transition-all duration-1000 delay-500 ${
                        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
                    }`}>
                        <p className="text-xl md:text-3xl lg:text-4xl mb-8 text-ivory/90 font-light">
                            {t('hero.title')}
                        </p>
                    </div>

                    <div className={`transform transition-all duration-1000 delay-700 ${
                        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
                    }`}>
                        <p className="text-base md:text-xl lg:text-2xl mb-10 max-w-3xl mx-auto text-ivory/80 leading-relaxed">
                            {t('hero.description')}
                        </p>
                    </div>

                    {/* Enhanced CTA buttons */}
                    <div
                        className={`flex flex-col sm:flex-row gap-6 justify-center transform transition-all duration-1000 delay-900 ${
                            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
                        }`}>
                        <button
                            onClick={() => openBookingWithActivity('general')}
                            className="group bg-blush text-taupe px-8 py-4 rounded-full font-semibold hover:bg-blush/90 transform hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-blush/25 relative overflow-hidden">
                            <span className="relative z-10">{t('hero.cta')}</span>
                            <div
                                className="absolute inset-0 bg-gradient-to-r from-blush to-ivory opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                        </button>

                    </div>
                </div>

                {/* Enhanced scroll indicator */}
                <div className="absolute bottom-8 left-0 right-0 flex justify-center">
                    <div
                        className="text-ivory animate-bounce cursor-pointer hover:scale-110 transition-transform duration-300 flex flex-col items-center"
                        onClick={() => document.getElementById('about')?.scrollIntoView({behavior: 'smooth'})}>
                        <ChevronDown className="w-8 h-8 animate-bounce"/>
                        <span className="text-sm mt-2 opacity-75">{t('common.scrollDown')}</span>
                    </div>
                </div>
            </section>

            {/* Section divider */}
            <div className="w-full h-px bg-emerald-600/20"></div>

            {/* Enhanced About Section */}
            <section id="about" className="py-20 lg:py-32 bg-ivory relative overflow-hidden">
                {/* Background decoration */}
                <div
                    className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blush/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div
                    className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-teal/10 to-transparent rounded-full translate-y-1/2 -translate-x-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        {/* Left content with enhanced animations */}
                        <div className={`space-y-8 transform transition-all duration-1000 delay-200 ${
                            scrollY > 300 ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
                        }`}>
                            <div>
                                <h2 className="text-4xl lg:text-6xl font-bold text-taupe mb-6 leading-tight">
                                    {t('about.title')}
                                </h2>
                                <p className="text-lg lg:text-xl text-taupe/80 leading-relaxed">
                                    {t('about.subtitle')}
                                </p>
                            </div>

                            {/* Enhanced achievement cards */}
                            <div className="grid grid-cols-2 gap-4 lg:gap-6">
                                {achievements.map((achievement, index) => (
                                    <div key={index}
                                         className={`group text-center p-6 bg-gradient-to-br from-blush/20 to-blush/10 rounded-2xl border border-blush/30 hover:border-blush/50 transition-all duration-500 hover:scale-105 hover:shadow-xl transform ${
                                             scrollY > 400 ? 'animate-fadeInScale' : ''
                                         }`}
                                         style={{animationDelay: `${index * 150}ms`}}>
                                        <div
                                            className="text-3xl lg:text-4xl font-bold text-emerald-600 mb-2 group-hover:scale-110 transition-transform duration-300">
                                            {achievement.number}
                                        </div>
                                        <div className="text-sm lg:text-base text-taupe/70 font-medium">
                                            {achievement.label}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={() => openBookingWithActivity('neuropsychology')}
                                className="group bg-emerald-600 hover:bg-emerald-700 text-ivory px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl relative overflow-hidden">
                                <span className="relative z-10">{t('contact.schedule')}</span>
                                <div
                                    className="absolute inset-0 bg-gradient-to-r from-emerald-700 to-teal opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </button>
                        </div>

                        {/* Right content with enhanced styling */}
                        <div className={`transform transition-all duration-1000 delay-400 ${
                            scrollY > 300 ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
                        }`}>
                            <div className="relative">
                                {/* Decorative elements */}
                                <div
                                    className="absolute -top-4 -left-4 w-8 h-8 bg-emerald-600/20 rounded-full animate-pulse"></div>
                                <div
                                    className="absolute -bottom-4 -right-4 w-6 h-6 bg-blush/40 rounded-full animate-float"></div>

                                <div
                                    className="glass-morphism p-8 lg:p-12 rounded-3xl border border-blush/30 hover:border-blush/50 transition-all duration-500 group">
                                    <h3 className="text-2xl lg:text-3xl font-semibold mb-6 text-taupe group-hover:text-emerald-600 transition-colors duration-300">
                                        {t('about.whyTitle')}
                                    </h3>
                                    <p className="text-taupe/80 leading-relaxed text-lg">
                                        {t('about.description')}
                                    </p>

                                    {/* Quote decoration */}
                                    <div className="mt-6 flex items-center space-x-2 text-emerald-600">
                                        <div className="w-12 h-0.5 bg-emerald-600"></div>
                                        <Brain className="w-5 h-5"/>
                                        <div className="w-12 h-0.5 bg-emerald-600"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section divider */}
            <div className="w-full h-px bg-emerald-600/20"></div>

            {/* Enhanced Services Section */}
            <section id="services" className="py-20 lg:py-32 bg-soft-cream relative">
                {/* Floating decoration elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div
                        className="absolute top-1/4 left-10 w-20 h-20 bg-emerald-600/5 rounded-full animate-float"></div>
                    <div className="absolute top-1/2 right-20 w-16 h-16 bg-blush/10 rounded-full animate-float"
                         style={{animationDelay: '1s'}}></div>
                    <div className="absolute bottom-1/4 left-1/4 w-12 h-12 bg-teal/10 rounded-full animate-float"
                         style={{animationDelay: '2s'}}></div>
                </div>

                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    {/* Enhanced header */}
                    <div className={`text-center mb-20 transform transition-all duration-1000 ${
                        scrollY > 800 ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                    }`}>
                        <h2 className="text-4xl lg:text-6xl font-bold text-taupe mb-6 leading-tight">
                            {t('services.title')}
                        </h2>
                        <p className="text-lg lg:text-2xl text-taupe/70 max-w-3xl mx-auto leading-relaxed">
                            {t('services.subtitle')}
                        </p>

                        {/* Decorative line */}
                        <div className="flex items-center justify-center mt-8">
                            <div className="h-0.5 w-20 bg-emerald-600"></div>
                            <div className="mx-4">
                                <Users className="w-6 h-6 text-emerald-600"/>
                            </div>
                            <div className="h-0.5 w-20 bg-emerald-600"></div>
                        </div>
                    </div>

                    {/* Enhanced services grid */}
                    <div className="max-w-5xl mx-auto">
                        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6 lg:gap-8">
                            {t('services.list').map((item, index) => (
                                <div key={index}
                                     className={`group relative p-6 lg:p-8 bg-ivory rounded-2xl hover:shadow-2xl transition-all duration-500 hover:scale-105 border border-blush/20 hover:border-emerald-600/30 transform ${
                                         scrollY > 900 ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                                     }`}
                                     style={{animationDelay: `${index * 100}ms`}}>

                                    {/* Gradient overlay on hover */}
                                    <div
                                        className="absolute inset-0 bg-gradient-to-br from-emerald-600/5 to-teal/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                    <div className="relative z-10 flex items-start gap-4">
                                        {/* Enhanced bullet point */}
                                        <div className="flex-shrink-0 mt-2">
                                            <div
                                                className="w-3 h-3 bg-emerald-600 rounded-full group-hover:scale-125 transition-transform duration-300 group-hover:shadow-lg group-hover:shadow-emerald-600/25"></div>
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1">
                                            <span
                                                className="text-taupe/90 group-hover:text-taupe text-lg font-medium leading-relaxed transition-colors duration-300">
                                                {item}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Decorative corner */}
                                    <div
                                        className="absolute top-3 right-3 w-2 h-2 bg-gradient-to-br from-emerald-600 to-teal rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Additional methods section */}
                    <div className={`mt-20 text-center transform transition-all duration-1000 delay-500 ${
                        scrollY > 1000 ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                    }`}>
                        <div
                            className="max-w-4xl mx-auto p-8 lg:p-12 glass-morphism rounded-3xl border border-blush/30">
                            <h3 className="text-2xl lg:text-3xl font-semibold text-taupe mb-8">
                                {t('services.methods.title')}
                            </h3>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                                {t('services.methods.items').map((method, index) => (
                                    <div key={index} className="text-center group">
                                        <div
                                            className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-emerald-600 to-teal rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                            <Award className="w-8 h-8 text-ivory"/>
                                        </div>
                                        <p className="text-taupe/80 font-medium group-hover:text-taupe transition-colors duration-300">
                                            {method}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section divider */}
            <div className="w-full h-px bg-emerald-600/20"></div>

            {/* Family Psychologist Section */}
            <section id="family-psychologist" className="py-20 lg:py-32 bg-ivory relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blush/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-teal/10 to-transparent rounded-full translate-y-1/2 -translate-x-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className={`space-y-8 transform transition-all duration-1000 delay-200 ${
                            scrollY > 1400 ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
                        }`}>
                            <div>
                                <div className="flex items-center mb-4">
                                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-teal rounded-2xl flex items-center justify-center shadow-lg">
                                        <Heart className="w-8 h-8 text-ivory"/>
                                    </div>
                                </div>
                                <h2 className="text-4xl lg:text-6xl font-bold text-taupe mb-6 leading-tight">
                                    {t('familyPsychologist.title')}
                                </h2>
                                <p className="text-lg lg:text-xl text-emerald-600 font-medium mb-6">
                                    {t('familyPsychologist.subtitle')}
                                </p>
                                <p className="text-lg text-taupe/80 leading-relaxed">
                                    {t('familyPsychologist.description')}
                                </p>
                            </div>

                            <button
                                onClick={() => openBookingWithActivity('familyPsychologist')}
                                className="group bg-emerald-600 hover:bg-emerald-700 text-ivory px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl relative overflow-hidden">
                                <span className="relative z-10">{t('familyPsychologist.cta')}</span>
                                <div className="absolute inset-0 bg-gradient-to-r from-emerald-700 to-teal opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </button>
                        </div>

                        <div className={`transform transition-all duration-1000 delay-400 ${
                            scrollY > 1400 ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
                        }`}>
                            <div className="relative">
                                <div className="absolute -top-4 -left-4 w-8 h-8 bg-emerald-600/20 rounded-full animate-pulse"></div>
                                <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-blush/40 rounded-full animate-float"></div>

                                <div className="glass-morphism p-8 lg:p-12 rounded-3xl border border-blush/30 hover:border-blush/50 transition-all duration-500">
                                    <div className="space-y-4">
                                        {t('familyPsychologist.benefits').map((benefit, index) => (
                                            <div key={index} className="flex items-start gap-4 group">
                                                <div className="flex-shrink-0 mt-1">
                                                    <div className="w-2 h-2 bg-emerald-600 rounded-full group-hover:scale-150 transition-transform duration-300"></div>
                                                </div>
                                                <span className="text-taupe/90 group-hover:text-taupe text-lg leading-relaxed transition-colors duration-300">
                                                    {benefit}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section divider */}
            <div className="w-full h-px bg-emerald-600/20"></div>

            {/* Child Psychologist Section */}
            <section id="child-psychologist" className="py-20 lg:py-32 bg-soft-cream relative overflow-hidden">
                <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-teal/10 to-transparent rounded-full -translate-y-1/2 -translate-x-1/2"></div>
                <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-tl from-blush/10 to-transparent rounded-full translate-y-1/2 translate-x-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className={`transform transition-all duration-1000 delay-200 lg:order-2 ${
                            scrollY > 1800 ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
                        }`}>
                            <div className="space-y-8">
                                <div>
                                    <div className="flex items-center mb-4">
                                        <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-teal rounded-2xl flex items-center justify-center shadow-lg">
                                            <Smile className="w-8 h-8 text-ivory"/>
                                        </div>
                                    </div>
                                    <h2 className="text-4xl lg:text-6xl font-bold text-taupe mb-6 leading-tight">
                                        {t('childPsychologist.title')}
                                    </h2>
                                    <p className="text-lg lg:text-xl text-emerald-600 font-medium mb-6">
                                        {t('childPsychologist.subtitle')}
                                    </p>
                                    <p className="text-lg text-taupe/80 leading-relaxed">
                                        {t('childPsychologist.description')}
                                    </p>
                                </div>

                                <button
                                    onClick={() => openBookingWithActivity('childPsychologist')}
                                    className="group bg-emerald-600 hover:bg-emerald-700 text-ivory px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl relative overflow-hidden">
                                    <span className="relative z-10">{t('childPsychologist.cta')}</span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-700 to-teal opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                </button>
                            </div>
                        </div>

                        <div className={`transform transition-all duration-1000 delay-400 lg:order-1 ${
                            scrollY > 1800 ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
                        }`}>
                            <div className="relative">
                                <div className="absolute -top-4 -right-4 w-8 h-8 bg-emerald-600/20 rounded-full animate-pulse"></div>
                                <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-blush/40 rounded-full animate-float"></div>

                                <div className="glass-morphism p-8 lg:p-12 rounded-3xl border border-blush/30 hover:border-blush/50 transition-all duration-500">
                                    <div className="space-y-4">
                                        {t('childPsychologist.benefits').map((benefit, index) => (
                                            <div key={index} className="flex items-start gap-4 group">
                                                <div className="flex-shrink-0 mt-1">
                                                    <div className="w-2 h-2 bg-emerald-600 rounded-full group-hover:scale-150 transition-transform duration-300"></div>
                                                </div>
                                                <span className="text-taupe/90 group-hover:text-taupe text-lg leading-relaxed transition-colors duration-300">
                                                    {benefit}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section divider */}
            <div className="w-full h-px bg-emerald-600/20"></div>

            {/* Gifted Children Groups Section */}
            <section id="gifted-children" className="py-20 lg:py-32 bg-ivory relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blush/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-teal/10 to-transparent rounded-full translate-y-1/2 -translate-x-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className={`space-y-8 transform transition-all duration-1000 delay-200 ${
                            scrollY > 2200 ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
                        }`}>
                            <div>
                                <div className="flex items-center mb-4">
                                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-teal rounded-2xl flex items-center justify-center shadow-lg relative">
                                        <Star className="w-6 h-6 text-ivory absolute top-2 left-2"/>
                                        <Sparkles className="w-6 h-6 text-ivory absolute bottom-2 right-2"/>
                                    </div>
                                </div>
                                <h2 className="text-4xl lg:text-6xl font-bold text-taupe mb-6 leading-tight">
                                    {t('giftedChildren.title')}
                                </h2>
                                <p className="text-lg lg:text-xl text-emerald-600 font-medium mb-6">
                                    {t('giftedChildren.subtitle')}
                                </p>
                                <p className="text-lg text-taupe/80 leading-relaxed">
                                    {t('giftedChildren.description')}
                                </p>
                            </div>

                            <button
                                onClick={() => openBookingWithActivity('giftedChildren')}
                                className="group bg-gradient-to-r from-emerald-600 to-teal hover:from-emerald-700 hover:to-emerald-800 text-ivory px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl relative overflow-hidden">
                                <span className="relative z-10 flex items-center gap-2">
                                    <Sparkles className="w-5 h-5"/>
                                    {t('giftedChildren.cta')}
                                </span>
                            </button>
                        </div>

                        <div className={`transform transition-all duration-1000 delay-400 ${
                            scrollY > 2200 ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
                        }`}>
                            <div className="relative">
                                <div className="absolute -top-4 -left-4 w-8 h-8 bg-emerald-600/20 rounded-full animate-pulse"></div>
                                <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-blush/40 rounded-full animate-float"></div>

                                <div className="glass-morphism p-8 lg:p-12 rounded-3xl border border-blush/30 hover:border-blush/50 transition-all duration-500 relative">
                                    <div className="absolute top-4 right-4 opacity-10">
                                        <Sparkles className="w-12 h-12 text-emerald-600"/>
                                    </div>
                                    <div className="space-y-4 relative z-10">
                                        {t('giftedChildren.benefits').map((benefit, index) => (
                                            <div key={index} className="flex items-start gap-4 group">
                                                <div className="flex-shrink-0 mt-1">
                                                    <div className="w-2 h-2 bg-emerald-600 rounded-full group-hover:scale-150 transition-transform duration-300"></div>
                                                </div>
                                                <span className="text-taupe/90 group-hover:text-taupe text-lg leading-relaxed transition-colors duration-300">
                                                    {benefit}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section divider */}
            <div className="w-full h-px bg-emerald-600/20"></div>

            {/* Professional Certificates Section */}
            <section id="certificates" className="py-20 lg:py-32 bg-ivory relative overflow-hidden">
                {/* Background decoration */}
                <div
                    className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-teal/10 to-transparent rounded-full -translate-y-1/2 -translate-x-1/2"></div>
                <div
                    className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-tl from-blush/10 to-transparent rounded-full translate-y-1/2 translate-x-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    {/* Enhanced header */}
                    <div className={`text-center mb-20 transform transition-all duration-1000 ${
                        scrollY > 1000 ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                    }`}>
                        <h2 className="text-4xl lg:text-6xl font-bold text-taupe mb-6 leading-tight">
                            {t('certificates.title')}
                        </h2>
                        <p className="text-lg lg:text-2xl text-taupe/70 max-w-3xl mx-auto leading-relaxed">
                            {t('certificates.subtitle')}
                        </p>

                        {/* Decorative line */}
                        <div className="flex items-center justify-center mt-8">
                            <div className="h-0.5 w-20 bg-emerald-600"></div>
                            <div className="mx-4">
                                <Award className="w-6 h-6 text-emerald-600"/>
                            </div>
                            <div className="h-0.5 w-20 bg-emerald-600"></div>
                        </div>
                    </div>

                    {/* Certificates grid - showing only 3 + placeholder */}
                    <div className={`transform transition-all duration-1000 delay-300 ${
                        scrollY > 1100 ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                    }`}>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
                            {/* First 3 certificates */}
                            {[...Array(3)].map((_, index) => (
                                <div key={index}
                                     onClick={() => openCertificateGallery(index)}
                                     className="group relative bg-ivory rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 border border-blush/20 hover:border-emerald-600/30 overflow-hidden cursor-pointer">

                                    {/* Certificate image */}
                                    <div className="aspect-[3/4] relative overflow-hidden">
                                        <img
                                            src={`https://cdn.hydralabs.ru/clients/neuro/certificates/${index}.png`}
                                            alt={`Certificate ${index + 1}`}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                            loading="lazy"
                                        />

                                        {/* Overlay with zoom icon */}
                                        <div
                                            className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                            <div
                                                className="bg-emerald-600 text-ivory p-3 rounded-full opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100 transition-all duration-300">
                                                <Expand className="w-6 h-6"/>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Certificate number */}
                                    <div
                                        className="absolute top-3 left-3 bg-emerald-600 text-ivory px-2 py-1 rounded-full text-xs font-semibold">
                                        {index + 1}
                                    </div>
                                </div>
                            ))}

                            {/* Placeholder card - View All Gallery */}
                            <div
                                onClick={() => openCertificateGallery(0)}
                                className="group relative bg-gradient-to-br from-emerald-600 to-teal rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 overflow-hidden cursor-pointer">

                                {/* Placeholder content */}
                                <div className="aspect-[3/4] relative overflow-hidden flex items-center justify-center p-6">
                                    <div className="text-center text-ivory">
                                        <div className="w-16 h-16 mx-auto mb-4 bg-ivory/20 rounded-full flex items-center justify-center group-hover:bg-ivory/30 transition-colors duration-300">
                                            <Award className="w-8 h-8 group-hover:scale-110 transition-transform duration-300"/>
                                        </div>
                                        <h3 className="text-lg font-semibold mb-2 group-hover:scale-105 transition-transform duration-300">
                                            {t('certificates.viewAll')}
                                        </h3>
                                        <p className="text-ivory/80 text-sm leading-relaxed">
                                            13 {language === 'ru' ? 'сертификатов' : 'certificates'}
                                        </p>
                                        
                                        {/* Decorative elements */}
                                        <div className="absolute top-4 right-4 w-8 h-8 bg-ivory/10 rounded-full opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        <div className="absolute bottom-4 left-4 w-6 h-6 bg-ivory/10 rounded-full opacity-30 group-hover:opacity-70 transition-opacity duration-300"></div>
                                    </div>
                                    
                                    {/* Hover overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                </div>

                                {/* '+' indicator */}
                                <div className="absolute top-3 left-3 bg-ivory/20 text-ivory px-2 py-1 rounded-full text-xs font-semibold backdrop-blur-sm">
                                    +10
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* View all certificates button */}
                    <div className={`text-center mt-12 transform transition-all duration-1000 delay-500 ${
                        scrollY > 1200 ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                    }`}>
                        <button
                            onClick={() => openCertificateGallery(0)}
                            className="group bg-emerald-600 hover:bg-emerald-700 text-ivory px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl relative overflow-hidden">
                            <span className="relative z-10">{t('certificates.viewAll')}</span>
                            <div
                                className="absolute inset-0 bg-gradient-to-r from-emerald-700 to-teal opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </button>
                    </div>
                </div>
            </section>

            {/* Section divider */}
            <div className="w-full h-px bg-emerald-600/20"></div>

            {/* Enhanced Testimonials Section */}
            <section id="testimonials" className="py-20 lg:py-32 bg-ivory relative overflow-hidden">
                {/* Background decorations */}
                <div className="absolute top-0 left-0 w-full h-full">
                    <div
                        className="absolute top-20 right-10 w-32 h-32 bg-gradient-to-br from-emerald-600/5 to-transparent rounded-full animate-pulse"></div>
                    <div
                        className="absolute bottom-20 left-10 w-24 h-24 bg-gradient-to-tr from-blush/10 to-transparent rounded-full animate-float"></div>
                </div>

                <div className="max-w-6xl mx-auto px-4 relative z-10">
                    {/* Enhanced header */}
                    <div className={`text-center mb-20 transform transition-all duration-1000 ${
                        scrollY > 1200 ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                    }`}>
                        <h2 className="text-4xl lg:text-6xl font-bold text-taupe mb-6 leading-tight">
                            {t('testimonials.title')}
                        </h2>
                        <p className="text-lg lg:text-2xl text-taupe/70 max-w-3xl mx-auto leading-relaxed">
                            {t('testimonials.subtitle')}
                        </p>
                    </div>

                    {/* Enhanced testimonial card */}
                    <div className={`relative max-w-4xl mx-auto transform transition-all duration-1000 delay-300 ${
                        scrollY > 1200 ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                    }`}>
                        <div className="relative">
                            {/* Decorative quotes */}
                            <div
                                className="absolute -top-6 -left-6 text-6xl text-emerald-600/20 font-serif select-none">
                                "
                            </div>
                            <div
                                className="absolute -bottom-6 -right-6 text-6xl text-emerald-600/20 font-serif select-none rotate-180">
                                "
                            </div>

                            <div
                                className="glass-morphism p-8 lg:p-12 rounded-3xl border border-blush/30 hover:border-blush/50 transition-all duration-500 group">
                                {/* Stars with enhanced animation */}
                                <div className="flex justify-center mb-6">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i}
                                              className="w-6 h-6 text-emerald-500 fill-current mx-1 hover:scale-125 transition-transform duration-300 cursor-pointer"
                                              style={{animationDelay: `${i * 100}ms`}}/>
                                    ))}
                                </div>

                                {/* Enhanced testimonial text */}
                                <div
                                    className="cursor-pointer group-testimonial"
                                    onClick={() => openReviewModal(t('testimonials.items')[activeTestimonial])}
                                >
                                    <p className="text-xl lg:text-2xl text-taupe/90 mb-8 italic leading-relaxed text-center font-light hover:text-emerald-600 transition-colors duration-300">
                                        "{truncateText(t('testimonials.items')[activeTestimonial].text, 150)}"
                                        {t('testimonials.items')[activeTestimonial].text.length > 150 && (
                                            <span className="text-emerald-600 font-medium ml-2 hover:underline">
                                                {language === 'ru' ? 'Читать полностью' : 'Read more'}
                                            </span>
                                        )}
                                    </p>
                                </div>

                                {/* Enhanced author */}
                                <div className="text-center">
                                    <div className="flex items-center justify-center mb-4">
                                        <div className="w-12 h-0.5 bg-emerald-600"></div>
                                        <div className="mx-4">
                                            <div className="w-3 h-3 bg-emerald-600 rounded-full"></div>
                                        </div>
                                        <div className="w-12 h-0.5 bg-emerald-600"></div>
                                    </div>
                                    <p className="text-emerald-600 font-semibold text-lg">
                                        {t('testimonials.items')[activeTestimonial].author}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Enhanced navigation dots */}
                        <div className="flex flex-wrap justify-center mt-10 gap-3 max-w-xs sm:max-w-none mx-auto">
                            {t('testimonials.items').map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveTestimonial(index)}
                                    className={`w-4 h-4 rounded-full transition-all duration-300 hover:scale-125 ${
                                        index === activeTestimonial
                                            ? 'bg-emerald-600 shadow-lg shadow-emerald-600/25'
                                            : 'bg-taupe/30 hover:bg-taupe/50'
                                    }`}
                                />
                            ))}
                        </div>

                        {/* Navigation arrows */}
                        <button
                            onClick={() => setActiveTestimonial(prev => prev === 0 ? t('testimonials.items').length - 1 : prev - 1)}
                            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-ivory rounded-full shadow-lg flex items-center justify-center text-emerald-600 hover:bg-emerald-600 hover:text-ivory transition-all duration-300 hover:scale-110"
                        >
                            <ChevronDown className="w-6 h-6 rotate-90"/>
                        </button>

                        <button
                            onClick={() => setActiveTestimonial(prev => prev === t('testimonials.items').length - 1 ? 0 : prev + 1)}
                            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-ivory rounded-full shadow-lg flex items-center justify-center text-emerald-600 hover:bg-emerald-600 hover:text-ivory transition-all duration-300 hover:scale-110"
                        >
                            <ChevronDown className="w-6 h-6 -rotate-90"/>
                        </button>
                    </div>
                </div>
            </section>

            {/* Section divider */}
            <div className="w-full h-px bg-emerald-600/20"></div>

            {/* Enhanced Contact Section */}
            <section id="contact"
                     className="py-20 lg:py-32 bg-gradient-to-br from-emerald-600 via-teal to-emerald-700 text-ivory relative overflow-hidden">
                {/* Animated background elements */}
                <div className="absolute inset-0">
                    <div className="absolute top-20 left-20 w-40 h-40 bg-ivory/5 rounded-full animate-blob"></div>
                    <div className="absolute bottom-20 right-20 w-32 h-32 bg-blush/10 rounded-full animate-blob"
                         style={{animationDelay: '2s'}}></div>
                    <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-ivory/5 rounded-full animate-float"
                         style={{animationDelay: '4s'}}></div>
                </div>

                <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
                    {/* Enhanced header */}
                    <div className={`mb-16 lg:mb-20 transform transition-all duration-1000 ${
                        scrollY > 1500 ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                    }`}>
                        <h2 className="text-4xl lg:text-6xl font-bold mb-6 text-shadow-lg leading-tight">
                            {t('contact.title')}
                        </h2>
                        <p className="text-lg lg:text-2xl mb-8 text-ivory/90 max-w-3xl mx-auto leading-relaxed">
                            {t('contact.subtitle')}
                        </p>

                        {/* Decorative divider */}
                        <div className="flex items-center justify-center">
                            <div className="h-0.5 w-20 bg-ivory/50"></div>
                            <div className="mx-4">
                                <Phone className="w-6 h-6 text-ivory/70"/>
                            </div>
                            <div className="h-0.5 w-20 bg-ivory/50"></div>
                        </div>
                    </div>

                    {/* Enhanced contact cards */}
                    <div className="grid md:grid-cols-3 gap-8 lg:gap-12 mb-16">
                        {[
                            {
                                icon: MessageCircle,
                                title: 'WhatsApp',
                                href: 'https://wa.me/79036734651',
                                value: t('contact.methods.whatsapp'),
                                isButton: false
                            },
                            {
                                icon: Calendar,
                                title: 'Schedule',
                                href: '#',
                                value: t('contact.schedule'),
                                isButton: true
                            },
                            {
                                icon: MessageSquare,
                                title: 'Telegram',
                                href: '#',
                                value: t('contact.methods.telegram'),
                                isButton: false
                            },
                        ].map((contact, index) => (
                            <div key={contact.title}
                                 className={`group glass-morphism p-8 rounded-3xl border border-ivory/20 hover:border-ivory/40 transition-all duration-500 hover:scale-105 hover:shadow-2xl transform ${
                                     scrollY > 1600 ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                                 }`}
                                 style={{animationDelay: `${index * 200}ms`}}>

                                {/* Icon with enhanced animation */}
                                <div
                                    className="w-16 h-16 mx-auto mb-6 bg-ivory/10 rounded-full flex items-center justify-center group-hover:bg-ivory/20 transition-all duration-300 group-hover:scale-110">
                                    <contact.icon
                                        className="w-8 h-8 group-hover:scale-110 transition-transform duration-300"/>
                                </div>

                                <p className="font-semibold mb-4 text-lg">{contact.title}</p>


                                <button
                                    onClick={contact.isButton ? () => openBookingWithActivity('general') : () => {window.open(contact.href, '_blank')}}
                                    className="text-blush hover:text-ivory transition-colors duration-300 font-medium bg-ivory/10 px-6 py-3 rounded-full hover:bg-ivory/20">
                                    {contact.value}
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Enhanced CTA button */}
                    <div className={`transform transition-all duration-1000 delay-600 ${
                        scrollY > 1600 ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                    }`}>
                        <button
                            onClick={() => openBookingWithActivity('general')}
                            className="group relative bg-blush hover:bg-ivory text-taupe px-10 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-blush/25 text-lg overflow-hidden">
                            <span className="relative z-10 group-hover:text-emerald-600 transition-colors duration-300">
                                {t('hero.cta')}
                            </span>
                            <div
                                className="absolute inset-0 bg-gradient-to-r from-ivory to-blush opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                            {/* Ripple effect */}
                            <div
                                className="absolute inset-0 bg-white/20 scale-0 group-hover:scale-100 rounded-full transition-transform duration-500 ease-out"></div>
                        </button>
                    </div>
                </div>
            </section>

            {/* Section divider */}
            <div className="w-full h-px bg-emerald-600/20"></div>

            {/* Enhanced Footer */}
            <footer className="bg-white text-taupe py-16 relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0">
                    <div
                        className="absolute top-0 right-0 w-64 h-64 bg-emerald-600/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                    <div
                        className="absolute bottom-0 left-0 w-48 h-48 bg-blush/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                </div>

                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
                        {/* Brand section */}
                        <div className="space-y-6">
                            <h3 className="text-3xl font-bold text-emerald-600">{t('hero.name')}</h3>
                            <p className="text-taupe/80 leading-relaxed text-lg">{t('footer.description')}</p>
                            <div className="flex items-center space-x-2">
                                <div className="w-12 h-0.5 bg-emerald-600"></div>
                                <Brain className="w-5 h-5 text-emerald-600"/>
                                <div className="w-12 h-0.5 bg-emerald-600"></div>
                            </div>
                        </div>

                        {/* Contact section */}
                        <div className="space-y-6">
                            <h4 className="text-xl font-semibold mb-6 text-emerald-600">{t('nav.contact')}</h4>
                            <div className="space-y-4">
                                <div className="flex items-center space-x-3 group">
                                    <MessageCircle
                                        className="w-5 h-5 text-emerald-600 group-hover:scale-110 transition-transform duration-300"/>
                                    <button onClick={() => {window.open('https://wa.me/79036734651', '_blank')}}
                                            className="text-taupe/80 hover:text-taupe transition-colors">
                                        {t('contact.methods.whatsapp')}
                                    </button>
                                </div>
                                <div className="flex items-center space-x-3 group">
                                    <MessageSquare
                                        className="w-5 h-5 text-emerald-600 group-hover:scale-110 transition-transform duration-300"/>
                                    <button onClick={() => openBookingWithActivity('general')}
                                            className="text-taupe/80 hover:text-taupe transition-colors">
                                        {t('contact.methods.telegram')}
                                    </button>
                                </div>
                                <div className="flex items-center space-x-3 group">
                                    <Phone
                                        className="w-5 h-5 text-emerald-600 group-hover:scale-110 transition-transform duration-300"/>
                                    <button onClick={() => openBookingWithActivity('general')}
                                            className="text-taupe/80 hover:text-taupe transition-colors">
                                        {t('contact.methods.phone')}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Navigation section */}
                        <div className="space-y-6">
                            <h4 className="text-xl font-semibold mb-6 text-emerald-600">{t('footer.navigation')}</h4>
                            <div className="space-y-3">
                                {['about', 'services', 'testimonials'].map((item) => (
                                    <a key={item} href={`#${item}`}
                                       className="group flex items-center space-x-2 text-taupe/80 hover:text-taupe transition-all duration-300">
                                        <div
                                            className="w-1 h-1 bg-emerald-600 rounded-full group-hover:w-3 transition-all duration-300"></div>
                                        <span>{t(`nav.${item}`)}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Enhanced footer bottom */}
                    <div className="border-t border-taupe/20 mt-12 pt-8">
                        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                            <p className="text-taupe/70 text-center md:text-left">
                                &copy; {new Date().getFullYear()} {t('hero.name')}. {t('footer.rights')}
                            </p>
                            <div className="flex items-center space-x-4">
                                <span className="text-taupe/50 text-sm">{t('footer.madeWith')}</span>
                                <div
                                    className="w-4 h-4 bg-gradient-to-br from-emerald-600 to-blush rounded-full animate-pulse"></div>
                                <span className="text-taupe/50 text-sm">{t('footer.forChildren')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>


            {/* Certificate Gallery Modal */}
            {showCertificateGallery && (
                <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 animate-fadeInFast">
                    <div className="relative max-w-5xl w-full max-h-[90vh] overflow-hidden">
                        {/* Close button */}
                        <button
                            onClick={closeCertificateGallery}
                            className="absolute top-6 right-6 z-10 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300 hover:scale-110 group backdrop-blur-sm"
                        >
                            <X className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300"/>
                        </button>

                        {/* Navigation arrows */}
                        <button
                            onClick={prevCertificate}
                            className="absolute left-6 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                        >
                            <ChevronLeft className="w-6 h-6"/>
                        </button>

                        <button
                            onClick={nextCertificate}
                            className="absolute right-6 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                        >
                            <ChevronRight className="w-6 h-6"/>
                        </button>

                        {/* Certificate image */}
                        <div className="flex items-center justify-center h-full">
                            <img
                                src={`https://cdn.hydralabs.ru/clients/neuro/certificates/${activeCertificate}.png`}
                                alt={`Certificate ${activeCertificate + 1}`}
                                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                            />
                        </div>

                        {/* Certificate counter and title */}
                        <div
                            className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full backdrop-blur-sm">
                            <span className="text-sm font-medium">
                                {activeCertificate + 1} / 13
                            </span>
                        </div>

                        {/* Thumbnail navigation */}
                        <div className="absolute bottom-6 left-6 right-6">
                            <div className="flex justify-center space-x-2 overflow-x-auto py-2">
                                {[...Array(13)].map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setActiveCertificate(index)}
                                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all duration-300 ${
                                            index === activeCertificate
                                                ? 'ring-2 ring-emerald-400 scale-110'
                                                : 'hover:scale-105 opacity-70 hover:opacity-100'
                                        }`}
                                    >
                                        <img
                                            src={`https://cdn.hydralabs.ru/clients/neuro/certificates/${index}.png`}
                                            alt={`Certificate ${index + 1} thumbnail`}
                                            className="w-full h-full object-cover"
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Keyboard hints */}
                        <div className="absolute top-6 left-6 text-white/70 text-sm">
                            <div className="bg-black/50 px-3 py-2 rounded-lg backdrop-blur-sm">
                                <div>← → Navigate</div>
                                <div>ESC Close</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Review Modal */}
            {showReviewModal && selectedReview && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeInFast">
                    <div className="relative bg-ivory rounded-3xl w-full max-w-3xl max-h-[85vh] overflow-hidden shadow-2xl transform animate-fadeInFast">
                        {/* Enhanced close button */}
                        <button
                            onClick={closeReviewModal}
                            className="absolute top-6 right-6 z-10 bg-taupe/80 hover:bg-taupe text-ivory p-3 rounded-full transition-all duration-300 hover:scale-110 group"
                        >
                            <X className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300"/>
                        </button>

                        {/* Modal content */}
                        <div className="p-8 lg:p-12 overflow-y-auto max-h-full">
                            {/* Decorative quotes */}
                            <div className="absolute -top-4 -left-4 text-6xl text-emerald-600/20 font-serif select-none">
                                "
                            </div>
                            <div className="absolute -bottom-4 -right-4 text-6xl text-emerald-600/20 font-serif select-none rotate-180">
                                "
                            </div>

                            {/* Stars */}
                            <div className="flex justify-center mb-6">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i}
                                          className="w-6 h-6 text-emerald-500 fill-current mx-1"/>
                                ))}
                            </div>

                            {/* Full review text */}
                            <div className="relative z-10">
                                <p className="text-lg lg:text-xl text-taupe/90 mb-8 italic leading-relaxed text-center font-light">
                                    "{selectedReview.text}"
                                </p>

                                {/* Author */}
                                <div className="text-center">
                                    <div className="flex items-center justify-center mb-4">
                                        <div className="w-12 h-0.5 bg-emerald-600"></div>
                                        <div className="mx-4">
                                            <div className="w-3 h-3 bg-emerald-600 rounded-full"></div>
                                        </div>
                                        <div className="w-12 h-0.5 bg-emerald-600"></div>
                                    </div>
                                    <p className="text-emerald-600 font-semibold text-lg">
                                        {selectedReview.author}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Booking Form Modal */}
            <BookingForm
                isOpen={showBooking}
                onClose={() => setShowBooking(false)}
                t={t}
                language={language}
                activityType={bookingActivityType}
            />
        </div>
    );
}

export default App;